import { useCallback, useEffect, useState } from "react";
import { useStopwatch } from "./StopwatchContext";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import stopwatchRepository from "../services/stopwatchRepository";

export interface SessionsForApi {
    client_id: string; // Assuming this is the client-generated ID
    table_id: number; // The ID of the table associated with this session
    hours: number;
    minutes: number;
    seconds: number;
    last_start_time?: string; // ISO string format for the last start time
    status: string; // 'active', 'finished', 'paused', etc.
}
const mapStatus = (state: string) => {
    switch (state) {
        case 'running':
            return 'active';
        case 'stopped':
            return 'finished';
        case 'paused':
            return 'paused';
        default:
            return state; // In case of any other state, just return it
    }
}

const SyncManager = () => {
    const { sessions, setSessions } = useStopwatch();
    const isOnline = useOnlineStatus(); // Use the hook to track connectivity
    const [isSyncing, setIsSyncing] = useState(false);
    console.log("RERENDING")
    const [retryCount, setRetryCount] = useState(0);


    const syncUnsyncedSessions = useCallback(async () => {
        const unsyncedSessions = sessions.filter(session => !session.synced);

        // Only sync if online, not already syncing and there are unsynced sessions
        if (!isOnline || isSyncing || unsyncedSessions.length === 0) {
            console.log("No unsynced sessions or currently offline/syncing. Skipping sync.");
            return;
        }

        if (unsyncedSessions.length > 0 && !isSyncing) {
            setIsSyncing(true);

            // 1. Translate frontend data to backend DTO format
            const sessionsForApi = unsyncedSessions.map(session => {
                // Convert milliseconds to H, M, S
                const totalSeconds = Math.floor(session.elapsedTime / 1000);
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;

                return {
                    client_id: session.id,
                    table_id: session.tableId,
                    hours: hours,
                    minutes: minutes,
                    seconds: seconds,
                    last_start_time: session.lastStartTime,
                    status: mapStatus(session.state),
                };
            });

            try {
                // 2. Send the translated data to your Laravel endpoint
                await stopwatchRepository.sync(sessionsForApi);
                console.table(sessionsForApi);
                console.log("Sync successful for sessions:", unsyncedSessions.map(s => s.id));

                // 3. Mark as synced on success (same as before)
                const syncedIds = unsyncedSessions.map(s => s.id);
                setSessions(prevSessions =>
                    prevSessions.map(s =>
                        syncedIds.includes(s.id) ? { ...s, synced: true } : s
                    )
                );

                setRetryCount(0);


            } catch (error) {
                console.error("Sync failed:", error);
                setRetryCount(prev => prev + 1);

            } finally {
                setIsSyncing(false);
            }
        }
    }, [sessions, isOnline, isSyncing, setSessions]);

    // Effect 1: Debounces changes made by the user
    useEffect(() => {
        // Don't run this if a retry cycle is active.
        if (retryCount > 0) return;

        const debounceTimer = setTimeout(syncUnsyncedSessions, 2000);
        return () => clearTimeout(debounceTimer);
    }, [sessions, retryCount, syncUnsyncedSessions]);

    // ✅ Effect 2: Handles the exponential backoff for server failures
    useEffect(() => {
        // Don't run if there's nothing to retry or if offline.
        if (retryCount === 0 || !isOnline) return;

        const delay = Math.min(60000, (2 ** retryCount) * 1000) + Math.random() * 1000;
        console.log(`Sync failed. Retrying in ${Math.round(delay / 1000)}s... (Attempt ${retryCount})`);

        const retryTimer = setTimeout(syncUnsyncedSessions, delay);
        return () => clearTimeout(retryTimer);

    }, [retryCount, isOnline, syncUnsyncedSessions]);

    return null; // This component renders nothing
};

export default SyncManager;
